import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Check,
  Clear,
  Send as SendIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { CommentType } from '../types/supabase';
import { Comment } from '../types/campagne';
import { 
  getCampaignComments, 
  addCampaignComment, 
  addCommentResponse, 
  toggleCommentResolution, 
  deleteCampaignComment 
} from '../lib/supabase/campaignCommentsService';
import { useAuth } from '../lib/supabase/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface SupabaseCommentSectionProps {
  campaignId: string;
  commentType: CommentType;
  isAdmin?: boolean;
}

export const SupabaseCommentSection: React.FC<SupabaseCommentSectionProps> = ({
  campaignId,
  commentType,
  isAdmin = false,
}) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [newResponses, setNewResponses] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  // Fonction pour charger les commentaires
  const loadComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await getCampaignComments(campaignId, commentType);
      
      if (error) {
        throw error;
      }
      
      setComments(data || []);
    } catch (err: any) {
      console.error('Erreur lors du chargement des commentaires:', err);
      setError(`Erreur lors du chargement des commentaires: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Charger les commentaires au montage du composant
  useEffect(() => {
    loadComments();
  }, [campaignId, commentType]);

  // Fonction pour ajouter un nouveau commentaire
  const handleAddComment = async () => {
    if (!newComment.trim() || !user) return;

    try {
      setSubmitting(true);
      setError(null);
      
      const authorName = user.email || 'Utilisateur';
      const { error } = await addCampaignComment(
        campaignId,
        commentType,
        newComment,
        authorName,
        user.id
      );

      if (error) {
        throw error;
      }

      setNewComment('');
      await loadComments(); // Recharger les commentaires
    } catch (err: any) {
      console.error('Erreur lors de l\'ajout du commentaire:', err);
      setError(`Erreur lors de l'ajout du commentaire: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction pour ajouter une réponse
  const handleAddResponse = async (commentId: string) => {
    const response = newResponses[commentId];
    if (!response?.trim() || !user) return;

    try {
      setSubmitting(true);
      setError(null);
      
      const authorName = user.email || 'Utilisateur';
      const { error } = await addCommentResponse(
        commentId,
        response,
        authorName,
        user.id
      );

      if (error) {
        throw error;
      }

      setNewResponses(prev => ({ ...prev, [commentId]: '' }));
      await loadComments(); // Recharger les commentaires
    } catch (err: any) {
      console.error('Erreur lors de l\'ajout de la réponse:', err);
      setError(`Erreur lors de l'ajout de la réponse: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction pour marquer un commentaire comme résolu
  const handleResolveComment = async (commentId: string, currentResolved: boolean) => {
    try {
      setError(null);
      const { error } = await toggleCommentResolution(commentId, !currentResolved);

      if (error) {
        throw error;
      }

      await loadComments(); // Recharger les commentaires
    } catch (err: any) {
      console.error('Erreur lors de la résolution du commentaire:', err);
      setError(`Erreur lors de la résolution du commentaire: ${err.message}`);
    }
  };

  // Fonction pour supprimer un commentaire
  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) return;

    try {
      setError(null);
      const { error } = await deleteCampaignComment(commentId);

      if (error) {
        throw error;
      }

      await loadComments(); // Recharger les commentaires
    } catch (err: any) {
      console.error('Erreur lors de la suppression du commentaire:', err);
      setError(`Erreur lors de la suppression du commentaire: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Commentaires
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Zone d'ajout de commentaire */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Ajouter un commentaire..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          sx={{ mb: 1 }}
          disabled={submitting}
        />
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={handleAddComment}
          disabled={!newComment.trim() || submitting}
        >
          {submitting ? 'Envoi...' : 'Commenter'}
        </Button>
      </Box>

      {/* Liste des commentaires */}
      <List>
        {comments.map((comment) => (
          <ListItem
            key={comment.id}
            sx={{
              flexDirection: 'column',
              alignItems: 'stretch',
              mb: 2,
              opacity: comment.isResolved ? 0.7 : 1,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                backgroundColor: comment.isResolved ? 'action.hover' : 'background.paper',
              }}
            >
              {/* En-tête du commentaire */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  {comment.author}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(comment.timestamp), 'dd/MM/yyyy HH:mm', { locale: fr })}
                  </Typography>
                  {isAdmin && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleResolveComment(comment.id, comment.isResolved)}
                        color={comment.isResolved ? "default" : "success"}
                        disabled={submitting}
                      >
                        {comment.isResolved ? <Clear /> : <Check />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteComment(comment.id)}
                        color="error"
                        disabled={submitting}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>

              {/* Contenu du commentaire */}
              <Typography variant="body1">{comment.text}</Typography>

              {/* Réponses */}
              {comment.responses && comment.responses.length > 0 && (
                <List sx={{ ml: 3, mt: 1 }}>
                  {comment.responses.map((response) => (
                    <ListItem key={response.id} sx={{ px: 0 }}>
                      <Paper elevation={0} sx={{ p: 1, backgroundColor: 'action.hover', width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="subtitle2" color="primary">
                            {response.author}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(response.timestamp), 'dd/MM/yyyy HH:mm', { locale: fr })}
                          </Typography>
                        </Box>
                        <Typography variant="body2">{response.text}</Typography>
                      </Paper>
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Zone de réponse */}
              {!comment.isResolved && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Répondre..."
                    value={newResponses[comment.id] || ''}
                    onChange={(e) =>
                      setNewResponses(prev => ({
                        ...prev,
                        [comment.id]: e.target.value
                      }))
                    }
                    disabled={submitting}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleAddResponse(comment.id)}
                    disabled={!newResponses[comment.id]?.trim() || submitting}
                  >
                    Répondre
                  </Button>
                </Box>
              )}
            </Paper>
          </ListItem>
        ))}
      </List>

      {comments.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
          Aucun commentaire pour le moment. Soyez le premier à commenter !
        </Typography>
      )}
    </Box>
  );
}; 